import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AccountAddress } from '@viewbid/ts-angular-client';

/**
 * Billing-address picker for the add-card form. The addresses render as an
 * always-visible option-row radiogroup (the invoice fulfillment step's
 * "Ship to" pattern) — the choices are on screen the moment the form opens,
 * replacing the old focus-to-open typeahead.
 */
@Component({
    selector: 'vb-address-selection',
    templateUrl: './address-selection.component.html',
    styleUrls: ['./address-selection.component.scss'],
})
export class AddressSelectionComponent implements OnChanges {
    @Input() public userAddresses: AccountAddress[] = [];
    @Input() public defaultAddresses!: string;
    @Output() public selectedAddressEvent: EventEmitter<AccountAddress | null> =
        new EventEmitter<AccountAddress | null>();

    /** Default-first copy of the input list (input order otherwise). */
    sortedAddresses: AccountAddress[] = [];
    selectedAddress: AccountAddress | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userAddresses'] || changes['defaultAddresses']) {
            this.sortedAddresses = [...(this.userAddresses ?? [])].sort(
                (a: AccountAddress, b: AccountAddress) =>
                    (this.isDefaultAddress(b.id) ? 1 : 0) -
                    (this.isDefaultAddress(a.id) ? 1 : 0),
            );
            // A refreshed list can drop the picked address — clear the stale
            // selection so the parent can't submit an id that no longer exists.
            if (
                this.selectedAddress &&
                !this.sortedAddresses.some(
                    (address: AccountAddress) =>
                        address.id === this.selectedAddress?.id,
                )
            ) {
                this.selectedAddress = null;
                this.selectedAddressEvent.emit(null);
            }
        }
    }

    isDefaultAddress(addressId?: string): boolean {
        if (!addressId || !this.defaultAddresses) return false;
        return this.defaultAddresses === addressId;
    }

    isSelected(address: AccountAddress): boolean {
        return !!address.id && address.id === this.selectedAddress?.id;
    }

    /** "123 Queen St W, Unit 4" — the option's main line (same composition
     *  as the fulfillment step's address rows). */
    addressLine1(address: AccountAddress): string {
        return [address.address1, address.address2]
            .filter((part?: string) => !!part)
            .join(', ');
    }

    /** "Toronto, ON  M6R 2B2" — the supporting line. */
    addressLine2(address: AccountAddress): string {
        const cityProvince: string = [address.city, address.province]
            .filter((part?: string) => !!part)
            .join(', ');
        return [cityProvince, address.postalCode]
            .filter((part?: string) => !!part)
            .join('  ');
    }

    selectAddress(address: AccountAddress): void {
        this.selectedAddress = address;
        this.selectedAddressEvent.emit(address);
    }
}
